import { useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormLabel } from '../../../shared/components/form-label';
import { TextInput } from '../../../shared/components/text-input';
import { ValidationMessage } from '../../../shared/components/validation-message';
import { loadTurnstile } from '../../../shared/utils/turnstile';
import { environment } from '../../../../environments/environment';
import type { WizardFormValues } from '../schemas';
import { StepHeading } from './step-heading';

export function Step3YourDetails() {
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext<WizardFormValues>();
  const widgetContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = widgetContainerRef.current;
    if (!container) return;
    let widgetId: string | undefined;
    let cancelled = false;

    loadTurnstile()
      .then(() => {
        if (cancelled || !window.turnstile) return;
        widgetId = window.turnstile.render(container, {
          sitekey: environment.turnstile.siteKey,
          callback: (token: string) => setValue('turnstile', token, { shouldValidate: true }),
          'error-callback': () => setValue('turnstile', null, { shouldValidate: true }),
          'expired-callback': () => setValue('turnstile', null, { shouldValidate: true }),
        });
      })
      .catch(() => {
        // If Turnstile fails to load (network/blocked), leave the field empty
        // so the user stays gated on this step with the validation message.
      });

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch {
          // Widget may have already been removed by the script — ignore.
        }
      }
    };
  }, [setValue]);

  return (
    <div className="space-y-5">
      <StepHeading
        title="Your details"
        description="We'll email you the budget. We don't share your details with anyone else."
      />

      <div>
        <FormLabel htmlFor="email">Email</FormLabel>
        <TextInput
          id="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          {...register('email')}
        />
        <ValidationMessage error={errors.email as never} message={errors.email?.message ?? ''} />
      </div>

      <div>
        <FormLabel htmlFor="phoneNumber">
          Phone <span className="text-hilevel-gray font-normal">(optional)</span>
        </FormLabel>
        <TextInput
          id="phoneNumber"
          type="tel"
          autoComplete="tel"
          inputMode="tel"
          {...register('phoneNumber')}
        />
        <ValidationMessage
          error={errors.phoneNumber as never}
          message={errors.phoneNumber?.message ?? ''}
        />
      </div>

      <div>
        <div ref={widgetContainerRef} data-testid="turnstile-widget" />
        <ValidationMessage
          error={errors.turnstile as never}
          message={errors.turnstile?.message ?? ''}
        />
      </div>
    </div>
  );
}
